/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { CertModel, FallBackPageModel } from '../../../shared/models';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { FallbackPagesApiService } from '../../../shared/http';
import { LogService, ModalService, NotifyService, RbacService } from '../../../../../core/services';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-fallback-page',
    templateUrl: './fallbackPages.component.html',
    styleUrls: ['./fallbackPages.component.style.scss']
})

export class FallbackPagesComponent extends BaseComponent {
    /**
     * To store the fallback pages.
     */
    public tableRows: FallBackPageModel[] = [];

    /**
     * To store the certificate data.
     */
    public certs: CertModel[];

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        certificates: [],
        types: [],
    };

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        private fallbackPagesApiService: FallbackPagesApiService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router
    ) {
        super();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Set up the data for the page.
     * @private
     */
    private setUpPageData(): void {
        this.fallbackPagesApiService.getAll()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.tableRows = data;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('FallbackPagesComponent setUpPage - failed to get page data ', error.message);
                }
            });
    }

    /**
     * Send user to add/edit page.
     * @param row
     */
    public addEditFallbackPage(row?: any): void {
        const url = row ? 'edit/' + row.id : 'add';
        this.router.navigate(
            [url],
            { relativeTo: this.route }).then();
    }

    /**
     * Confirm delete fallback page.
     * @param row
     */
    public confirmDelete(row: any): void {
        const confirmationObject = {
            title: 'fallback_pages_delete_page',
            message: 'fallback_pages_delete_page_message',
            callBack: this.deleteFallbackPage.bind(this, row.id),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete fallback page.
     * @param {string} id
     * @returns {Observable<any>}
     * @private
     */
    private deleteFallbackPage(id: string): Observable<any> {
        return new Observable((observer) => {
            this.fallbackPagesApiService.delete(id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.notifyService.removeNotificationById('fallback');
                        this.logService.error('FallBackPagesComponent: deleteFallbackPage',
                            'Unable to delete the fallback page.', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'fallback-pages-add-page': [{ path: '/fallback', methods: ['POST'] }],
            'fallback-pages-edit-page': [{ path: '/fallback/:id', methods: ['PUT'] }],
            'fallback-pages-delete-page': [{ path: '/fallback/:id', methods: ['DELETE'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
